<template>
    <section>
        <FormSection
            :title="visitDetails.TaskName"
            :full-height="false"
            :application-id="visitDetails.Application"
            :show-product-pill="true"
        >
            <template #content>
                <section class="p-5 d-flex flex-column text-center text-primary" v-if="isLoading">
                    <i class="fa fa-spinner fa-spin font-size-5xl me-2 animated slow flash infinite"></i>
                    <span class="mt-1">
                        <b>Loading...</b>
                    </span>
                </section>
                <section class="task-details position-relative" v-else>
                    <div class="container row m-0 p-1">
                        <div
                            v-if="visitDetails.isEmployeeCalendar && !visitDetails.IsNonVisitAssignment"
                            class="col-11 my-1 ps-4 ms-1"
                        >
                            <b>Patient Name: {{ visitDetails.PatientFirstName }} {{ visitDetails.PatientLastName }}</b>
                        </div>
                        <div v-if="visitDetails.IsNonVisitAssignment" class="col-11 my-1 ps-4 ms-1">
                            <b>User Name: {{ scheduleEvent.userFirstName }} {{ scheduleEvent.userLastName }}</b>
                        </div>
                        <div v-if="visitDetails.IsNonVisitAssignment" class="col-11 my-1 ps-4 ms-1">
                            <b>Branch: {{ scheduleEvent.branchName }}</b>
                        </div>
                        <div class="d-flex col-12 mb-3 justify-content-center align-items-center">
                            <div v-if="!visitDetails.IsNonVisitAssignment" class="col-3 form-group">
                                <label for="task-name"> Task: </label>
                                <select
                                    id="task-name"
                                    class="form-select"
                                    @change="disciplineTaskTypeChanged(disciplineTaskType)"
                                    v-model="disciplineTaskType"
                                    :disabled="isPlanner"
                                >
                                    <option
                                        v-for="(task, index) of taskDetails.disciplineTasks"
                                        :key="index"
                                        :value="task.value"
                                    >
                                        {{ task.text }}
                                    </option>
                                </select>
                            </div>
                            <div v-else class="col-3 form-group">
                                <label for="task-name"> Non-Visit Activity: </label>
                                <select id="task-name" class="form-select" v-model="nVATaskId" :disabled="isPlanner">
                                    <option
                                        v-for="(task, index) of taskDetails.disciplineTasks"
                                        :key="index"
                                        :value="task.value"
                                    >
                                        {{ task.text }}
                                    </option>
                                </select>
                            </div>
                            <div class="form-group form-check custom-checkbox col-3 mt-4 ps-5">
                                <div v-if="!visitDetails.IsNonVisitAssignment" class="pe-5">
                                    <input
                                        type="checkbox"
                                        id="billable"
                                        class="form-check-input"
                                        v-model="scheduleEvent.isBillable"
                                        :disabled="isPlanner"
                                    />
                                    <label class="form-check-label" for="billable"> Billable </label>
                                </div>
                                <div v-else class="pe-5">
                                    <input
                                        type="checkbox"
                                        id="paidStatus"
                                        class="form-check-input"
                                        v-model="scheduleEvent.paidStatus"
                                        :disabled="isPlanner"
                                    />
                                    <label class="form-check-label" for="paidStatus"> Paid Status </label>
                                </div>
                                <div class="">
                                    <input
                                        type="checkbox"
                                        id="payable"
                                        class="form-check-input"
                                        v-model="scheduleEvent.isPayable"
                                        :disabled="isPlanner || visitDetails.IsNonVisitAssignment"
                                    />
                                    <label class="form-check-label" for="payable"> Payable </label>
                                </div>
                            </div>
                            <div class="col-3 form-group">
                                <label for="travel-start-time"> Travel Start Time: </label>
                                <div>
                                    <div class="input-group flex-nowrap">
                                        <TimePicker id="travel-start-time" @update-props="changeTravelStartTime">
                                            <input
                                                placeholder="hh:mm AM"
                                                :class="`form-control`"
                                                type="text"
                                                autocomplete="off"
                                                maxlength="8"
                                                v-model="scheduleEvent.travelTimeIn"
                                                :disabled="isPlanner"
                                            />
                                        </TimePicker>
                                    </div>
                                </div>
                            </div>
                            <div class="col-3 form-group">
                                <label for="travel-end-time"> Travel End Time: </label>
                                <div>
                                    <div class="input-group flex-nowrap">
                                        <TimePicker id="travel-end-time" @update-props="changeTravelEndTime">
                                            <input
                                                placeholder="hh:mm AM"
                                                :class="`form-control`"
                                                type="text"
                                                autocomplete="off"
                                                maxlength="8"
                                                v-model="scheduleEvent.travelTimeOut"
                                                :disabled="isPlanner"
                                            />
                                        </TimePicker>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="d-flex col-12 mb-3 justify-content-center align-items-center">
                            <div v-if="!visitDetails.IsNonVisitAssignment" class="col-3 form-group">
                                <label for="schedule-date"> Schedule Date: </label>
                                <div class="input-group flex-nowrap">
                                    <DatePicker :getDate="handleDateChange">
                                        <div class="input-group date datepickerInput">
                                            <input
                                                id="schedule-date"
                                                type="text"
                                                name="scheduleDateTime"
                                                class="form-control rounded-0 border-end-0"
                                                placeholder="MM/DD/YYYY"
                                                :value="formatDate(scheduleEvent.eventDate)"
                                                @change="handleTypedDateChange"
                                                :disabled="!areFieldsEnabled"
                                            />
                                            <div class="input-group-addon input-group-append">
                                                <div class="input-group-text">
                                                    <i class="fa fa-calendar"></i>
                                                </div>
                                            </div>
                                        </div>
                                    </DatePicker>
                                </div>
                            </div>
                            <div v-else class="col-3 form-group">
                                <label for="schedule-date"> NVA Date: </label>
                                <div class="input-group flex-nowrap">
                                    <DatePicker :getDate="handleDateChange">
                                        <div class="input-group date datepickerInput">
                                            <input
                                                id="nva-date"
                                                type="text"
                                                name="nvaDateTime"
                                                class="form-control rounded-0 border-end-0"
                                                placeholder="MM/DD/YYYY"
                                                :value="formatDate(scheduleEvent.nvaStartDate)"
                                                @change="handleTypedDateChange"
                                            />
                                            <div class="input-group-addon input-group-append">
                                                <div class="input-group-text">
                                                    <i class="fa fa-calendar"></i>
                                                </div>
                                            </div>
                                        </div>
                                    </DatePicker>
                                </div>
                            </div>
                            <div v-if="!visitDetails.IsNonVisitAssignment" class="col-3 form-group">
                                <label for="actual-date"> Actual Date: </label>
                                <div class="input-group flex-nowrap">
                                    <DatePicker :getDate="handleDateChange">
                                        <div class="input-group date datepickerInput">
                                            <input
                                                id="actual-date"
                                                type="text"
                                                name="actualDateTime"
                                                class="form-control rounded-0 border-end-0"
                                                placeholder="MM/DD/YYYY"
                                                :value="formatDate(scheduleEvent.visitDate)"
                                                @change="handleTypedDateChange"
                                                :disabled="!areFieldsEnabled"
                                            />
                                            <div class="input-group-addon input-group-append">
                                                <div class="input-group-text">
                                                    <i class="fa fa-calendar"></i>
                                                </div>
                                            </div>
                                        </div>
                                    </DatePicker>
                                </div>
                            </div>
                            <div v-else class="col-3 form-group">
                                <label for="actual-date"> Paid Date: </label>
                                <div class="input-group flex-nowrap">
                                    <DatePicker :getDate="handleDateChange">
                                        <div class="input-group date datepickerInput">
                                            <input
                                                id="nvapaid-date"
                                                type="text"
                                                name="nvaPaidDate"
                                                class="form-control rounded-0 border-end-0"
                                                placeholder="MM/DD/YYYY"
                                                :value="
                                                    formatDate(
                                                        scheduleEvent.nvaPaidDate?.startsWith('0001-01-01')
                                                            ? null
                                                            : scheduleEvent.nvaPaidDate
                                                    )
                                                "
                                                @change="handleTypedDateChange"
                                                :disabled="!areFieldsEnabled"
                                            />
                                            <div class="input-group-addon input-group-append">
                                                <div class="input-group-text">
                                                    <i class="fa fa-calendar"></i>
                                                </div>
                                            </div>
                                        </div>
                                    </DatePicker>
                                </div>
                            </div>
                            <div class="form-group col-3">
                                <label for="visit-start-time">
                                    {{ visitDetails.IsNonVisitAssignment ? "NVA Start Time:" : "Visit Time In:" }}
                                </label>
                                <TimePicker @update-props="changeVisitTimeIn">
                                    <input
                                        id="visit-start-time"
                                        type="text"
                                        class="form-control"
                                        autocomplete="off"
                                        v-model="scheduleEvent.timeIn"
                                        placeholder="hh:mm AM"
                                        :disabled="!areFieldsEnabled"
                                    />
                                </TimePicker>
                            </div>
                            <div class="form-group col-3">
                                <label for="visit-end-time"
                                    >{{ visitDetails.IsNonVisitAssignment ? "NVA End Time:" : "Visit Time Out:" }}
                                </label>
                                <TimePicker @update-props="changeVisitTimeOut">
                                    <input
                                        placeholder="hh:mm AM"
                                        id="visit-end-time"
                                        type="text"
                                        class="form-control"
                                        autocomplete="off"
                                        v-model="scheduleEvent.timeOut"
                                        :disabled="!areFieldsEnabled"
                                    />
                                </TimePicker>
                            </div>
                        </div>
                        <div
                            :class="
                                !visitDetails.IsNonVisitAssignment
                                    ? 'd-flex col-12 mb-3 justify-content-center align-items-center'
                                    : 'd-flex col-12 mb-3 align-items-center'
                            "
                        >
                            <div v-if="!visitDetails.IsNonVisitAssignment" class="form-group col-3">
                                <label for="status"> Status: </label>
                                <select
                                    class="form-select"
                                    id="status"
                                    v-model="scheduleEvent.status"
                                    @change="handleChangeStatus(scheduleEvent.status)"
                                    :disabled="isPlanner || visitDetails.IsNonVisitAssignment"
                                >
                                    <option
                                        v-for="(status, index) of taskDetails.statuses"
                                        :value="status.value"
                                        :key="index"
                                    >
                                        {{ status.text }}
                                    </option>
                                </select>
                            </div>
                            <div v-if="!visitDetails.IsNonVisitAssignment" class="form-group col-3">
                                <label for="assigned-to-list"> Assigned To: </label>
                                <TypeaheadInput
                                    id="assigned-to-list"
                                    :getData="employeeListFilter"
                                    :value="scheduleEmployee"
                                    v-on:valueChanged="changescheduleEmployee"
                                    placeholder="Type to Select User"
                                    :show-all-by-default="true"
                                    :open-on-focus="true"
                                    :disabled="isPlanner || visitDetails.IsNonVisitAssignment"
                                />
                            </div>
                            <div v-if="!visitDetails.IsNonVisitAssignment" class="form-group col-3">
                                <label for="surcharge"> Surcharge: </label>
                                <input
                                    id="surcharge"
                                    type="text"
                                    class="form-control"
                                    placeholder="Enter Amount"
                                    v-model="scheduleEvent.surcharge"
                                    number-only
                                    maxlength="7"
                                    :disabled="isPlanner || visitDetails.IsNonVisitAssignment"
                                />
                            </div>
                            <div class="form-group col-3">
                                <label for="associated-mileage"> Associated Mileage: </label>
                                <input
                                    id="associated-mileage"
                                    type="text"
                                    class="form-control"
                                    placeholder="Enter Amount"
                                    v-model="scheduleEvent.associatedMileage"
                                    number-only
                                    maxlength="7"
                                    :disabled="isPlanner"
                                />
                            </div>
                        </div>
                        <div
                            v-if="!visitDetails.IsNonVisitAssignment"
                            class="d-flex col-12 mb-3 justify-content-start align-items-center"
                        >
                            <div class="form-group col-3">
                                <label for="service-location"> Service Location (Q Codes): </label>
                                <select
                                    id="service-location"
                                    class="form-select"
                                    v-model="scheduleEvent.serviceLocation"
                                    @change="serviceLocationChange(scheduleEvent.serviceLocation)"
                                    :disabled="isPlanner || visitDetails.IsNonVisitAssignment"
                                >
                                    <option
                                        v-for="(location, index) of taskDetails.serviceLocations"
                                        :value="location.value"
                                        :key="index"
                                    >
                                        {{ location.text }}
                                    </option>
                                </select>
                            </div>
                            <div v-if="!visitDetails.IsNonVisitAssignment" class="form-group col-3">
                                <label for="auto-calculated-travel-time"> Auto-Calculated Travel Time: </label>
                                <input
                                    id="auto-calculated-travel-time"
                                    type="text"
                                    class="form-control"
                                    number-only
                                    disabled
                                />
                            </div>
                            <div v-if="!visitDetails.IsNonVisitAssignment" class="form-group col-3">
                                <label for="auto-calculated-mileage"> Auto-Calculated Mileage </label>
                                <input
                                    id="auto-calculated-mileage"
                                    type="text"
                                    class="form-control"
                                    number-only
                                    disabled
                                />
                            </div>
                            <div class="form-group col-3" v-if="showPhyicianField">
                                <label for="physician-name"> Physician </label>
                                <TypeaheadInput
                                    id="physician-name"
                                    placeholder="Start Typing"
                                    :getData="getPhysicianList"
                                    :value="physician"
                                    :initValue="physician"
                                    :disabled="isPlanner || visitDetails.IsNonVisitAssignment"
                                >
                                </TypeaheadInput>
                            </div>
                        </div>
                        <div
                            v-if="!visitDetails.IsNonVisitAssignment"
                            class="d-flex col-12 mb-3 justify-content-start align-items-center"
                        >
                            <div class="form-group col-3" v-if="isReturnedWithSignature">
                                <label for="received-date"> Received Date: </label>
                                <DatePicker>
                                    <div class="input-group date datepickerInput">
                                        <input
                                            id="received-date"
                                            type="text"
                                            class="form-control"
                                            placeholder="MM/DD/YYYY"
                                            date-format-only
                                            :disabled="isPlanner || visitDetails.IsNonVisitAssignment"
                                        />
                                        <div class="input-group-addon input-group-append">
                                            <div class="input-group-text">
                                                <i class="fa fa-calendar"></i>
                                            </div>
                                        </div>
                                    </div>
                                </DatePicker>
                            </div>
                            <div class="form-group col-3" v-if="isReturnedWithSignature()">
                                <label for="physician-signature-date"> Physician Signature Date: </label>
                                <DatePicker>
                                    <div class="input-group date datepickerInput">
                                        <input
                                            id="physician-signature-date"
                                            type="text"
                                            class="form-control"
                                            placeholder="MM/DD/YYYY"
                                            :value="scheduleEvent.physicianSignedDate"
                                            date-format-only
                                            :disabled="isPlanner || visitDetails.IsNonVisitAssignment"
                                        />
                                        <div class="input-group-addon input-group-append">
                                            <div class="input-group-text">
                                                <i class="fa fa-calendar"></i>
                                            </div>
                                        </div>
                                    </div>
                                </DatePicker>
                            </div>
                            <div class="form-group col-3">
                                <label for="confirmation-status"> Confirmation Status </label>
                                <select
                                    id="confirmation-status"
                                    class="form-select"
                                    v-model="confirmationStatus"
                                    :disabled="!areFieldsEnabled || visitDetails.IsNonVisitAssignment"
                                >
                                    <option :value="0">Select Confirmation Status</option>
                                    <option
                                        v-for="[index, status] in enumToMap(confirmationStatusEnum)"
                                        :value="status.value"
                                        :key="status.value"
                                    >
                                        {{ status.name }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <div
                            v-if="!visitDetails.IsNonVisitAssignment"
                            class="d-flex col-12 mb-3 justify-content-start align-items-center"
                        >
                            <div class="form-group col-12">
                                <label for="episode-selection"> Episode Selection </label>
                                <div class="form-group form-check custom-checkbox col-11 mt-2 ms-3">
                                    <div>
                                        <input
                                            type="checkbox"
                                            id="another-episode"
                                            class="form-check-input"
                                            v-model="isEpisodeSelectionChecked"
                                            :disabled="isPlanner"
                                        />
                                        <label class="form-check-label" for="another-episode">
                                            Check here to move visit/document to another episode
                                        </label>
                                        <div class="form-group col-12 mt-2" v-if="isEpisodeSelectionChecked">
                                            <label for="episode-list"> Episode List: </label>
                                            <select
                                                id="episode-list"
                                                class="form-select"
                                                v-model="scheduleEvent.episodeId"
                                                @change="onEpisodeListChange(scheduleEvent.episodeId)"
                                                :disabled="isPlanner"
                                            >
                                                <option
                                                    v-for="(episode, index) of taskDetails.patientEpisodes"
                                                    :key="index"
                                                    :value="episode.value"
                                                >
                                                    {{ episode.text }}
                                                </option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <section class="d-flex flex-column col-12 p-2">
                            <div class="form-group">
                                <label v-if="!visitDetails.IsNonVisitAssignment" for="comments">
                                    Comments <i class="ms-2 fas fa-sticky-note yellow"></i> (Yellow Sticky Note)
                                </label>
                                <label v-else for="comments"> Comments </label>
                                <CommentInput
                                    id="comments"
                                    input-name="referral-comments"
                                    placeholder="Enter Comments"
                                    :classList="commentInputClass"
                                    :application="visitDetails.Application"
                                    v-on:resultUpdatedAppended="resultUpdatedAppended"
                                    v-on:resultUpdated="resultUpdated"
                                    :showTemplateTypeahead="false"
                                    :showTemplateDropdown="false"
                                    :result="scheduleEvent.comments"
                                    :disabled="isPlanner"
                                />
                            </div>
                        </section>
                        <section v-if="!visitDetails.IsNonVisitAssignment" class="d-flex flex-column col-12 p-2">
                            <div class="form-group">
                                <label for="return-reason">
                                    Return Reason <i class="fas fa-sticky-note ms-2 red-note"></i> (Red Sticky Note)
                                </label>
                                <div v-if="returnReasons.length > 0">
                                    <div v-for="(reasonCode, index) of returnReasons" :key="index">
                                        <div class="d-flex justify-content-between">
                                            <div class="fw-bold">
                                                {{ reasonCode.user }}
                                                <sup
                                                    ><i>{{ reasonCode.date }}</i></sup
                                                >
                                            </div>
                                        </div>
                                        <div>{{ reasonCode.reason }}</div>
                                    </div>
                                </div>
                                <div
                                    class="p-2 mb-2 bg-white rounded border border-secondary"
                                    v-if="!returnReasons.length"
                                >
                                    <div class="text-center fst-italic">No Return Reason Found</div>
                                </div>
                            </div>
                        </section>
                        <section v-if="!visitDetails.IsNonVisitAssignment" class="d-flex flex-column col-12 p-2">
                            <div class="form-group">
                                <VisitAttachmentsHH :visitDetails="visitDetails" :isPlanner="isPlanner" />
                            </div>
                        </section>
                    </div>
                    <div
                        class="d-flex justify-content-center mb-3 position-sticky bg-background-home-health-form-section p-2 z-index-10 bottom-0"
                        v-if="!isLoading"
                    >
                        <span v-if="isSavingVisit">
                            <button class="btn btn-primary">
                                <i class="fa fa-spinner fa-spin animated slow flash infinite"></i>
                            </button>
                        </span>
                        <span v-if="!isSavingVisit">
                            <button class="btn btn-primary" @click="updateVisitDetails()">Save</button>
                            <button type="button" class="ms-1 btn btn-md btn-outline-secondary" @click="closeDialog()">
                                Cancel
                            </button>
                        </span>
                    </div>
                </section>
            </template>
        </FormSection>
    </section>
</template>

<style scoped>
.yellow {
    color: #fff400;
}
.form-group {
    padding-left: 15px;
    padding-right: 10px;
}
</style>

<script lang="ts" src="./VisitDetailsHomeHealth.ts"></script>
<style src="../VisitDetailsHomeCare/VisitDetailsHomeCare.scss"></style>
